import { Card } from "@/components/ui/card"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useEffect } from "react"

interface CertStats {
  total: number
  active: number
  pending: number
}

export const CertStats = () => {
  const { data: stats, refetch, isLoading, error } = useQuery<CertStats>({
    queryKey: ['cert-stats'],
    queryFn: async () => {
      console.log('Fetching certification statistics...')
      
      // Get total count (excluding PENDING entries)
      const { data, error: totalError, count: totalCount } = await supabase
        .from('cert_applications')
        .select('*', { count: 'exact' })
        .not('domain', 'eq', 'PENDING')
      
      if (totalError) {
        console.error('Error fetching total count:', totalError)
        throw totalError
      }

      // Get active certifications count
      const { data: activeData, error: activeError, count: activeCount } = await supabase
        .from('cert_applications')
        .select('*', { count: 'exact' })
        .not('domain', 'eq', 'PENDING')
        .eq('status', 'approved')
      
      if (activeError) {
        console.error('Error fetching active count:', activeError)
        throw activeError
      }

      // Get pending applications count
      const { data: pendingData, error: pendingError, count: pendingCount } = await supabase
        .from('cert_applications')
        .select('*', { count: 'exact' })
        .not('domain', 'eq', 'PENDING')
        .eq('status', 'pending')
      
      if (pendingError) {
        console.error('Error fetching pending count:', pendingError)
        throw pendingError
      }

      return {
        total: totalCount || 0,
        active: activeCount || 0,
        pending: pendingCount || 0
      }
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Subscribe to real-time updates
  useEffect(() => {
    const channel = supabase
      .channel('cert-stats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cert_applications'
        },
        (payload) => {
          console.log('Certification stats changed:', payload)
          refetch()
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status)
      })

    return () => {
      console.log('Cleaning up realtime subscription')
      supabase.removeChannel(channel)
    }
  }, [refetch])

  if (error) {
    console.error('Error fetching certification stats:', error)
    return (
      <div className="grid grid-cols-3 gap-4 w-full max-w-2xl">
        <Card className="bg-red-500/10 border-red-500/20 p-4 col-span-3">
          <p className="text-sm text-red-400">获取统计数据时出错</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-3 gap-4 w-full max-w-2xl">
      <Card className="bg-white/5 border-white/10 p-4">
        <p className="text-sm text-gray-400">总认证数</p>
        <p className="text-3xl font-bold text-white mt-1">
          {isLoading ? '-' : stats?.total || '0'}
        </p>
      </Card>
      
      <Card className="bg-white/5 border-white/10 p-4">
        <p className="text-sm text-gray-400">活跃认证</p>
        <p className="text-3xl font-bold text-green-400 mt-1">
          {isLoading ? '-' : stats?.active || '0'}
        </p>
      </Card>
      
      <Card className="bg-white/5 border-white/10 p-4">
        <p className="text-sm text-gray-400">待审核</p>
        <p className="text-3xl font-bold text-yellow-400 mt-1">
          {isLoading ? '-' : stats?.pending || '0'}
        </p>
      </Card>
    </div>
  )
}